/*
 * The address book in the core API (mt-vox docs/api.md, "Contacts"):
 * contacts.list / save / delete / import / sync, events contacts.changed
 * and contacts.rejected. Same behaviour as the native core (core/src/contacts.c):
 *
 * A contact (mt-prov-sf docs/contacts.md): first_name, last_name,
 * display_name, company, title, department, phones, emails, addresses (each
 * item with an id), primary_phone, primary_email, tags, note, favorite. Apps
 * send the whole contact to contacts.save; it is compared with the copy held
 * and only what changed is queued (a patch, with the seq of the server's copy
 * it started from), so edits of different fields on two devices never
 * conflict. The list adds "name", the name to show.
 *
 * Edits are local first (KV "contacts.book") and queued. When the
 * provisioning config carries contacts_seq, the queue is posted to
 * POST /v1/contacts/changes and changes are fetched with
 * GET /v1/contacts?since=seq, so every device of the user has the same book.
 * The first sync sends what this browser held as an import (the server merges
 * duplicates) and takes a full copy. A device that leaves provisioning
 * forgets a synced book.
 */
import type { Core, CoreModule } from "../core/core";
import { CommandError, Command, Json } from "../core/types";
import type { BookContact, ContactBook, Credential, ProvClient } from "./client";
import type { KV } from "./store";

const KEY = "contacts.book";
const VERSION = 2;
const BATCH = 500;
const DEBOUNCE_MS = 200;
const RETRY_MIN_MS = 15_000;
const RETRY_MAX_MS = 300_000;
const MAX_ITEMS = 16;
const MAX_TAGS = 16;

type Obj = Record<string, unknown>;
type Item = Record<string, string>;

/** A queued edit: what is posted, and the contact after it (none: a delete) */
interface Queued {
  change: Obj;
  local?: BookContact;
}

interface Book {
  version: number;
  seq: number;
  synced: boolean;
  books: ContactBook[];
  contacts: BookContact[];
  queue: Queued[];
}

/* A list of a contact: phones, emails, addresses */
interface Kind {
  list: "phones" | "emails" | "addresses";
  prefix: string;
  primary?: "primary_phone" | "primary_email";
  fields: string[];
}

const KINDS: Kind[] = [
  { list: "phones", prefix: "p", primary: "primary_phone", fields: ["type", "number", "ext", "label"] },
  { list: "emails", prefix: "e", primary: "primary_email", fields: ["type", "address"] },
  { list: "addresses", prefix: "a", fields: ["type", "street", "city", "region", "postal_code", "country"] },
];
const DEFTYPE = "work";
const TEXTS = ["first_name", "last_name", "company", "title", "department", "note"];

export interface ContactsTimers {
  set(fn: () => void, ms: number): unknown;
  clear(t: unknown): void;
  now(): number;
}

const realTimers: ContactsTimers = {
  set: (fn, ms) => setTimeout(fn, ms),
  clear: (t) => clearTimeout(t as ReturnType<typeof setTimeout>),
  now: () => Date.now(),
};

/** prefix + "_" + n chars of [a-z0-9] */
export function newId(prefix: string, n: number): string {
  const bytes = crypto.getRandomValues(new Uint8Array(n));
  return prefix + "_" + Array.from(bytes, (b) => "abcdefghijklmnopqrstuvwxyz0123456789"[b % 36]).join("");
}

export const newContactId = () => newId("c", 16);

const isObj = (v: unknown): v is Obj => !!v && typeof v === "object" && !Array.isArray(v);
const str = (o: unknown, key: string): string => {
  const v = isObj(o) ? o[key] : undefined;
  return typeof v === "string" ? v : "";
};
const arr = (o: unknown, key: string): unknown[] | undefined => {
  const v = isObj(o) ? o[key] : undefined;
  return Array.isArray(v) ? v : undefined;
};
const objs = (o: unknown, key: string): Obj[] => (arr(o, key) ?? []).filter(isObj);

/** Digits, * # + (spaces, dashes, dots and parentheses removed) or a sip: URI;
 * a 10-digit NANP number (or 11 with a leading 1) becomes +1 and 10 digits,
 * as the server stores it. */
export function normalizeNumber(n: string): string {
  if (/^sips?:/i.test(n)) return n;
  const out = n.replace(/[ \t\-.()]/g, "");
  const d = out.length === 11 && out[0] === "1" ? out.slice(1) : out;
  return /^[2-9]\d\d[2-9]\d{6}$/.test(d) ? "+1" + d : out;
}

const validItemId = (id: string | undefined, prefix: string): id is string =>
  id !== undefined && new RegExp(`^${prefix}_[a-z0-9]{4,16}$`).test(id);

/** The name to show: display_name, "first last", company, first number or email */
export function displayName(c: unknown): string {
  if (str(c, "display_name")) return str(c, "display_name");
  const f = str(c, "first_name");
  const l = str(c, "last_name");
  if (f || l) return f + (f && l ? " " : "") + l;
  if (str(c, "company")) return str(c, "company");
  const phones = arr(c, "phones");
  if (phones?.length) return str(phones[0], "number");
  const emails = arr(c, "emails");
  return emails?.length ? str(emails[0], "address") : "";
}

// --- building a contact from what an app sent ---------------------------------

/* One item of a list, or null when it has nothing in it */
function itemBuild(k: Kind, input: Obj, id: string): Item | null {
  const o: Item = { id };
  let empty = true;
  for (const f of k.fields) {
    let v = str(input, f);
    if (f === "type") {
      o.type = v || DEFTYPE;
      continue;
    }
    if (f === "number") v = normalizeNumber(v);
    if (v && f !== "label") empty = false;
    o[f] = v;
  }
  if (empty || (k.prefix === "p" && !o.number)) return null;
  return o;
}

const itemEqual = (k: Kind, a: unknown, b: unknown) => k.fields.every((f) => str(a, f) === str(b, f));

/* The value that identifies an item for an app that sends no ids */
const itemValue = (k: Kind, o: unknown) => str(o, k.fields[1]);

const idIn = (list: unknown[], id: string) => list.some((o) => isObj(o) && str(o, "id") === id);

/*
 * The items of one list. An item keeps its id; an item without one (an app
 * that does not keep ids) takes the id of the old item with the same value,
 * else of the old item at the same place, else a new one: editing a number
 * is then an update of that number, not a remove and an add.
 */
function itemsBuild(k: Kind, input: Obj, old: BookContact | undefined): Item[] {
  const inarr = arr(input, k.list) ?? [];
  const oldarr = old ? objs(old, k.list) : [];
  const out: Item[] = [];
  for (let idx = 0; idx < inarr.length && out.length < MAX_ITEMS; idx++) {
    const it = inarr[idx];
    if (!isObj(it)) continue;
    const given = typeof it.id === "string" ? it.id : undefined;
    let id = "";
    if (validItemId(given, k.prefix) && !idIn(out, given)) id = given;

    // no id: the same value, then the same place
    const val = k.prefix === "p" ? normalizeNumber(str(it, "number")) : itemValue(k, it);
    const free = (o: Obj) => !idIn(out, str(o, "id")) && !idIn(inarr, str(o, "id"));
    if (!id && given === undefined) {
      const same = oldarr.find((o) => val && itemValue(k, o) === val && free(o));
      if (same) id = str(same, "id");
    }
    const at = oldarr[idx];
    if (!id && given === undefined && at && free(at)) id = str(at, "id");
    if (!id) id = newId(k.prefix, 8);

    const built = itemBuild(k, it, id);
    if (built) out.push(built);
  }
  return out;
}

/* An app of the first design: name, numbers (or number), email */
function fromFirstDesign(input: Obj): Obj {
  const o: Obj = { ...input };
  const name = str(input, "name");
  if (!("first_name" in input) && !("last_name" in input)) {
    const space = name.lastIndexOf(" ");
    if (space >= 0) {
      o.first_name = name.slice(0, space);
      o.last_name = name.slice(space + 1);
    } else {
      o.first_name = name;
    }
  }
  if (!arr(input, "phones")) {
    const nums = arr(input, "numbers");
    const phones: Obj[] = (nums ?? []).filter(isObj);
    if (!nums && str(input, "number")) phones.push({ type: "work", number: str(input, "number") });
    o.phones = phones;
  }
  if (!arr(input, "emails") && str(input, "email")) o.emails = [{ type: "work", address: str(input, "email") }];
  return o;
}

/* The primary of a list: the one asked, else the old one, else the first */
function pickPrimary(list: Item[], asked: unknown, old: unknown): string | null {
  if (typeof asked === "string" && idIn(list, asked)) return asked;
  if (typeof old === "string" && idIn(list, old)) return old;
  return list.length ? list[0].id : null;
}

/*
 * The contact to store from what an app sent, for id in book. old (the copy
 * held) gives item ids and the server's seq. null: nothing to show.
 */
export function buildContact(raw: Obj, id: string, book: string, old?: BookContact): BookContact | null {
  const input = "name" in raw || "numbers" in raw || "number" in raw || "email" in raw ? fromFirstDesign(raw) : raw;
  const c: Obj = {
    id,
    book,
    seq: typeof old?.seq === "number" ? old.seq : 0,
    first_name: str(input, "first_name"),
    last_name: str(input, "last_name"),
    display_name: str(input, "display_name") || null,
  };
  for (const f of TEXTS.slice(2)) c[f] = str(input, f);
  for (const k of KINDS) {
    const items = itemsBuild(k, input, old);
    c[k.list] = items;
    if (k.primary) c[k.primary] = pickPrimary(items, input[k.primary], old?.[k.primary]);
  }
  const tags: string[] = [];
  for (const t of arr(input, "tags") ?? []) {
    if (typeof t === "string" && t && tags.length < MAX_TAGS && !tags.includes(t)) tags.push(t);
  }
  c.tags = tags;
  c.favorite = input.favorite === true;
  return displayName(c) ? (c as unknown as BookContact) : null;
}

// --- what changed: the patch from old to new -----------------------------------

/* One list's {add, update, remove}, or null when it did not change */
function listEdit(k: Kind, oa: Obj[], na: Obj[]): Obj | null {
  const byId = (list: Obj[], id: string) => list.find((o) => str(o, "id") === id);
  const add: Obj[] = [];
  const update: Obj[] = [];
  const remove: string[] = [];
  for (const n of na) {
    const o = byId(oa, str(n, "id"));
    if (!o) add.push(n);
    else if (!itemEqual(k, o, n)) update.push(n);
  }
  for (const o of oa) if (!byId(na, str(o, "id"))) remove.push(str(o, "id"));
  const edit: Obj = {};
  if (add.length) edit.add = add;
  if (update.length) edit.update = update;
  if (remove.length) edit.remove = remove;
  return Object.keys(edit).length ? edit : null;
}

/** {set, phones: {add, update, remove}, ..., tags: {add, remove}}, or null when nothing changed */
export function buildPatch(old: BookContact, c: BookContact): Obj | null {
  const patch: Obj = {};
  const set: Obj = {};
  for (const f of TEXTS) if (str(old, f) !== str(c, f)) set[f] = str(c, f);
  if (str(old, "display_name") !== str(c, "display_name")) set.display_name = str(c, "display_name") || null;
  if ((old.favorite === true) !== (c.favorite === true)) set.favorite = c.favorite === true;
  for (const k of KINDS) {
    if (k.primary && str(old, k.primary) !== str(c, k.primary)) set[k.primary] = str(c, k.primary) || null;
    const edit = listEdit(k, objs(old, k.list), objs(c, k.list));
    if (edit) patch[k.list] = edit;
  }
  const strings = (c: BookContact) => (arr(c, "tags") ?? []).filter((t): t is string => typeof t === "string");
  const ot = strings(old);
  const nt = strings(c);
  const tadd = nt.filter((t) => !ot.includes(t));
  const trem = ot.filter((t) => !nt.includes(t));
  if (tadd.length || trem.length) patch.tags = { add: tadd, remove: trem };
  if (Object.keys(set).length) patch.set = set;
  return Object.keys(patch).length ? patch : null;
}

const emptyBook = (): Book => ({ version: VERSION, seq: 0, synced: false, books: [], contacts: [], queue: [] });

export class ContactsModule implements CoreModule {
  private book: Book = emptyBook();
  private loaded?: Promise<void>;
  private client?: ProvClient;
  private cred?: Credential;
  private server = false;
  private busy = false;
  private again = false;
  private refetch = false; // the server's copy differs: take it whole
  private backoff = 0;
  private timer?: unknown;

  constructor(
    private core: Core,
    private kv: KV,
    private timers: ContactsTimers = realTimers,
  ) {}

  command(c: Command): Promise<Json | void> | undefined {
    switch (c.cmd) {
      case "contacts.list":
        return this.ready().then(() => ({ ...this.describe(), seq: this.book.seq }));
      case "contacts.save":
        return this.ready().then(() => this.save(c.contact));
      case "contacts.delete":
        return this.ready().then(() => this.remove(String(c.contact_id ?? "")));
      case "contacts.import":
        return this.ready().then(() => this.importList(c.contacts));
      case "contacts.sync":
        if (!this.server) return Promise.reject(new CommandError("not_synced", "No address book on the server"));
        this.kick(0);
        return Promise.resolve();
      default:
        return undefined;
    }
  }

  stop(): void {
    if (this.timer !== undefined) this.timers.clear(this.timer);
    this.timer = undefined;
  }

  /** A config arrived (ProvModule): sync when the server's seq moved. */
  async onConfig(client: ProvClient, cred: Credential, seq: number | undefined): Promise<void> {
    await this.ready();
    this.client = client;
    this.cred = cred;
    if (seq === undefined) {
      this.server = false;
      return;
    }
    this.server = true;
    if (seq !== this.book.seq || !this.book.synced || this.book.queue.length) this.kick(0);
  }

  /** The device left provisioning: a synced book belongs to the user on the server. */
  async forget(): Promise<void> {
    await this.ready();
    this.stop();
    this.server = false;
    this.again = this.refetch = false;
    this.client = this.cred = undefined;
    if (!this.book.synced) return;
    this.book = emptyBook();
    await this.kv.del(KEY);
    this.emit();
  }

  // --- local book -----------------------------------------------------
  private ready(): Promise<void> {
    this.loaded ??= this.kv.get<Book>(KEY).then(async (b) => {
      if (!b) return;
      if ((b.version ?? 1) < VERSION) {
        this.loadFirstDesign(b as unknown as Obj);
        await this.persist();
        return;
      }
      this.book = { ...emptyBook(), ...b };
    });
    return this.loaded;
  }

  /*
   * A book of the first design: a synced one is on the server (in the new
   * format), so it is fetched again whole; a local one is converted and
   * imported at the first sync.
   */
  private loadFirstDesign(b: Obj): void {
    if (b.synced === true) {
      this.book.synced = true;
      this.book.seq = 0;
      return;
    }
    for (const c of objs(b, "contacts")) {
      const built = str(c, "id") ? buildContact(c, str(c, "id"), "personal") : null;
      if (built) this.put(built);
    }
  }

  private describe(): Json {
    return {
      contacts: this.book.contacts.map((c) => ({ ...c, name: displayName(c) })),
      books: this.book.books.map((b) => ({ ...b })),
      pending: this.book.queue.length,
      synced: this.book.synced && this.server,
    } as unknown as Json;
  }

  private emit(): void {
    this.core.event("contacts.changed", this.describe() as never);
  }

  private async persist(): Promise<void> {
    await this.kv.set(KEY, this.book);
  }

  private writable(book: string | undefined): boolean {
    return !book || book === "personal" || book === "device" || !!this.book.books.find((b) => b.id === book)?.writable;
  }

  private find(id: string): BookContact | undefined {
    return id ? this.book.contacts.find((c) => c.id === id) : undefined;
  }

  private put(c: BookContact): void {
    const i = this.book.contacts.findIndex((x) => x.id === c.id);
    if (i >= 0) this.book.contacts[i] = c;
    else this.book.contacts.push(c);
  }

  private drop(id: string): void {
    this.book.contacts = this.book.contacts.filter((x) => x.id !== id);
  }

  private async changed(): Promise<void> {
    await this.persist();
    this.emit();
    this.kick(DEBOUNCE_MS);
  }

  // --- the queue --------------------------------------------------------
  private queueCreate(contact: BookContact, imported: boolean): void {
    const change: Obj = { op: "create", edited_at: this.timers.now(), contact };
    if (imported) change.import = true;
    this.book.queue.push({ change, local: contact });
  }

  private queuePatch(patch: Obj, local: BookContact): void {
    const change = { ...patch, op: "patch", id: local.id, book: local.book, base_seq: local.seq ?? 0, edited_at: this.timers.now() };
    this.book.queue.push({ change, local });
  }

  private queueDelete(c: BookContact): void {
    this.book.queue.push({
      change: { op: "delete", id: c.id, book: c.book || "personal", base_seq: c.seq ?? 0, edited_at: this.timers.now() },
    });
  }

  // --- commands ---------------------------------------------------------
  private async save(input: unknown): Promise<Json> {
    if (!isObj(input)) throw new CommandError("invalid", "contact is required");
    const given = str(input, "id");
    const old = this.find(given);
    const book = (old ? old.book : str(input, "book")) || "personal";
    if (given && !old) throw new CommandError("not_found", "No such contact");
    if (!this.writable(book)) throw new CommandError("read_only", "This book is read only");
    const id = given || newContactId();
    const c = buildContact(input, id, book, old);
    if (!c) throw new CommandError("invalid", "A contact needs a name, a company, a number or an email");
    if (old) {
      const patch = buildPatch(old, c);
      if (!patch) return { id }; // nothing changed
      this.put(c);
      if (this.book.synced) this.queuePatch(patch, c);
    } else {
      this.put(c);
      if (this.book.synced) this.queueCreate(c, false);
    }
    await this.changed();
    return { id };
  }

  private async remove(id: string): Promise<void> {
    const c = this.find(id);
    if (!c) throw new CommandError("not_found", "No such contact");
    if (!this.writable(c.book)) throw new CommandError("read_only", "This book is read only");
    if (this.book.synced) this.queueDelete(c);
    this.drop(id);
    await this.changed();
  }

  /* Same contact: the same name to show (case-insensitive) and the same first number */
  private alreadyHave(c: BookContact): boolean {
    const name = displayName(c).toLowerCase();
    const n0 = str(arr(c, "phones")?.[0], "number");
    return this.book.contacts.some((o) => displayName(o).toLowerCase() === name && str(arr(o, "phones")?.[0], "number") === n0);
  }

  private async importList(list: unknown): Promise<Json> {
    if (!Array.isArray(list)) throw new CommandError("invalid", "contacts must be a list");
    let imported = 0;
    for (const input of list) {
      if (!isObj(input)) continue;
      const c = buildContact(input, newContactId(), "personal");
      if (!c || this.alreadyHave(c)) continue;
      this.put(c);
      if (this.book.synced) this.queueCreate(c, true);
      imported++;
    }
    if (imported) await this.changed();
    return { imported };
  }

  // --- sync -------------------------------------------------------------
  private kick(ms: number): void {
    if (!this.server) return;
    if (this.busy) {
      this.again = true;
      return;
    }
    if (this.timer !== undefined) this.timers.clear(this.timer);
    this.timer = this.timers.set(() => {
      this.timer = undefined;
      void this.run();
    }, ms);
  }

  /* Edits not sent yet: put them back on top of the server's copy */
  private reapplyQueue(): void {
    for (const q of this.book.queue) {
      if (q.local) this.put(q.local);
      else this.drop(str(q.change, "id"));
    }
  }

  private async run(): Promise<void> {
    if (!this.server || this.busy || !this.client || !this.cred) return;
    this.busy = true;
    let fetching = false;
    try {
      if (!this.book.synced) {
        // first sync: send what this browser held, then take a full copy
        for (const c of this.book.contacts) {
          if (!c.book || c.book === "personal" || c.book === "device") this.queueCreate(c, true);
        }
        this.book.synced = true;
        this.book.seq = 0;
        await this.persist();
      }
      let since = this.book.seq;
      if (this.book.queue.length) {
        const batch = this.book.queue.slice(0, BATCH);
        const r = await this.client.postContacts(
          this.cred,
          batch.map((q) => q.change),
        );
        // every posted change got an answer: drop them
        this.book.queue.splice(0, batch.length);
        for (const x of r.rejected ?? []) {
          this.core.event("contacts.rejected", { ...x } as never);
          this.refetch = true;
        }
        // a field that lost to a later edit, or a contact merged into another
        // one: our copy is not the server's, so take the server's whole
        for (const a of r.accepted ?? []) {
          if (Array.isArray(a.lost) || typeof a.merged_into === "string") this.refetch = true;
        }
        if (this.book.queue.length) {
          this.busy = false;
          await this.persist();
          this.kick(0);
          return;
        }
        if (this.refetch) since = 0;
        this.refetch = false;
      }
      fetching = true;
      for (;;) {
        const page = await this.client.contacts(this.cred, since);
        if (page.full) this.book.contacts = [];
        for (const c of page.contacts ?? []) {
          if (c.deleted) this.drop(c.id);
          else this.put(c);
        }
        if (Array.isArray(page.books)) this.book.books = page.books;
        this.reapplyQueue();
        this.book.seq = page.seq;
        this.emit();
        if (!page.more) break;
        since = page.seq;
      }
      await this.done(true);
    } catch (e) {
      if (fetching && (e as { status?: number }).status === 404) {
        this.server = false; // no line, so no book
        this.busy = false;
        return;
      }
      await this.done(false);
    }
  }

  private async done(ok: boolean): Promise<void> {
    this.busy = false;
    await this.persist();
    if (!ok) {
      this.backoff = this.backoff ? Math.min(this.backoff * 2, RETRY_MAX_MS) : RETRY_MIN_MS;
      this.timer = this.timers.set(() => {
        this.timer = undefined;
        void this.run();
      }, this.backoff);
      return;
    }
    this.backoff = 0;
    if (this.again || this.book.queue.length) {
      this.again = false;
      this.kick(0);
    }
  }
}
